import {
  ChangeDetectionStrategy,
  Component,
  PLATFORM_ID,
  forwardRef,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import { CurrencyPipe, NgClass, isPlatformBrowser } from '@angular/common';
import {
  getContrastColor as getContrastColorUtil,
  resolveCssVariableValue as resolveCssVariableValueUtil
} from '../../../utils/color.utils';

export enum DropdownStyle {
  Primary = 'primary',
  Secondary = 'secondary'
}

export enum DropdownSize {
  Small = 'small',
  Medium = 'medium',
  Big = 'big'
}

export interface DropdownItem {
  [key: string]: any;
}

@Component({
  selector: 'app-dropdown',
  imports: [CdkMenuTrigger, NgClass, CdkMenuItem, CdkMenu, CurrencyPipe],
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DropdownComponent<T extends DropdownItem>
  implements ControlValueAccessor
{
  private readonly platformId = inject(PLATFORM_ID);
  private activeOptionBackgroundColor: string | null = null;

  label: InputSignal<string> = input<string>('');
  icon: InputSignal<string> = input<string>('');
  empty: InputSignal<string> = input('No Options');
  placeholder: InputSignal<string> = input('Select');
  isArray: InputSignal<boolean> = input<boolean>(false);

  // Options Data
  options: InputSignal<T[]> = input.required<T[]>();
  optionValueName: InputSignal<string | null> = input<string | null>('id'); // Empty if Need Object As Value
  optionLabelName: InputSignal<string> = input('label');

  // Currency
  showOptionPrice: InputSignal<boolean> = input(false);
  optionPriceName: InputSignal<string> = input('price');
  optionPriceCurrencyCode: InputSignal<string> = input('USD');
  optionPriceDisplay: InputSignal<string> = input('$');
  optionPriceDigitsInfo: InputSignal<string> = input('1.0-2');

  // State
  isDisabled: WritableSignal<boolean> = signal<boolean>(false);
  isOpen: WritableSignal<boolean> = signal<boolean>(false);

  size: InputSignal<DropdownSize> = input<DropdownSize>(DropdownSize.Medium);
  style: InputSignal<DropdownStyle> = input<DropdownStyle>(
    DropdownStyle.Primary
  );

  selectedOptionIndex: WritableSignal<number> = signal<number>(-1);

  change: OutputEmitterRef<unknown> = output<unknown>();

  // Open/Close Handle
  onMenuClose() {
    this.isOpen.set(false);
  }

  onMenuOpen() {
    this.isOpen.set(true);
  }

  // Value Accessor Functions
  onChange!: (value: T | unknown) => void;
  onTouched!: () => void;

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  registerOnChange(fn: (value: T | unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(outsideValue: T | string): void {
    this.selectedOptionIndex.set(
      this.getOptionIndexByOutsideValue(outsideValue)
    );
  }

  onSelectOption(optionIndex: number): void {
    this.selectedOptionIndex.set(optionIndex);

    if (this.optionValueName() == null) {
      const selectedValue = this.options()[optionIndex];

      this.onChange(this.isArray() ? [selectedValue] : selectedValue);
      this.change.emit(this.isArray() ? [selectedValue] : selectedValue);
    } else {
      const selectedValue =
        this.options()[optionIndex][this.optionValueName()!];

      this.onChange(this.isArray() ? [selectedValue] : selectedValue);
      this.change.emit(this.isArray() ? [selectedValue] : selectedValue);
    }

    this.onTouched();
  }

  private getOptionIndexByOutsideValue(
    outsideValue: T | string,
    options: T[] = this.options()
  ): number {
    if (outsideValue == null) {
      return -1;
    }

    if (typeof outsideValue == 'object') {
      const stringifiedOutsideValue: string = JSON.stringify(outsideValue);

      return options.findIndex((option: T): boolean => {
        return JSON.stringify(option) === stringifiedOutsideValue;
      });
    } else {
      const optionValueName: string | null = this.optionValueName();
      if (optionValueName == null) {
        return -1;
      }

      return options.findIndex((option: T): boolean => {
        return option[optionValueName] === outsideValue;
      });
    }
  }

  getContrastColor(color: string | null | undefined): string {
    return getContrastColorUtil(color);
  }

  getActiveBackgroundColor(): string {
    const cachedColor = this.activeOptionBackgroundColor;
    if (cachedColor) {
      return cachedColor;
    }

    if (!isPlatformBrowser(this.platformId)) {
      this.activeOptionBackgroundColor = '#80ced7';
      return this.activeOptionBackgroundColor;
    }

    const computedStyle = getComputedStyle(document.documentElement);
    const backendColor = resolveCssVariableValueUtil(
      computedStyle.getPropertyValue('--backend-colors-primary').trim(),
      computedStyle
    );

    if (backendColor) {
      this.activeOptionBackgroundColor = backendColor;
      return backendColor;
    }

    const primaryButtonColor = resolveCssVariableValueUtil(
      computedStyle.getPropertyValue('--primary-button-bg').trim(),
      computedStyle
    );

    const resolvedColor = primaryButtonColor ?? '#80ced7';
    this.activeOptionBackgroundColor = resolvedColor;

    return resolvedColor;
  }
}
